import { useEffect, useMemo, useState } from "react";

import {
  Notification,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import { Product } from "@transfr-inc/dashboard-components/constants";
import { useUnloadWarning } from "@transfr-inc/dashboard-components/hooks";

import { useCurrentUser, useRole } from "@queries";
import { useStudents } from "@queries/organization-selectors";
import { useContainer } from "@src/contexts/container-context";
import { useAppStore } from "@src/stores/zustand/app-store";
import { CeInsights, DashboardBanner } from "@views/dashboard/components";

import {
  exportOrganizationCeStats,
  getCareerRatings,
  getCeOrganizationDailyStats,
  getCeOrganizationStats,
  getCeOrganizationTopCareers,
  getStatsFileName,
} from "@services";

import { useApiRequest } from "@lib/hooks";
import "./ce.tab.scss";

export const CeTab = () => {
  const { organizationService } = useContainer();

  const [orgStats, setOrgStats] = useState({ isLoading: true });
  const [dailyOrgStats, setOrgDailyStats] = useState({ isLoading: true });
  const [topCareers, setTopCareers] = useState();
  const [dateRange, setDateRange] = useState({});
  const currentUser = useCurrentUser();
  const role = useRole();
  const { organizationName, organizationCode, userId } = currentUser || {};
  const [organizationSelected, setOrganizationSelected] = useState();
  const setDashboardLoaded = useAppStore((state) => state.setDashboardLoaded);

  const students = useStudents({
    role,
    orgCode: organizationSelected?.code ?? organizationCode,
    userId,
  });
  const explorersMap = useMemo(
    () => new Map(students?.map((s) => [s.userId, s])),
    [students]
  );

  const getCeOrganizationSelectedStats = (orgCode, filters, isMultiOrg) => {
    Promise.all([
      getCeOrganizationDailyStats({
        organizationService,
        userId,
        role,
        orgCode,
        filters,
        getMultiOrg: isMultiOrg,
      }).then((response) => setOrgDailyStats({ isLoading: false, response })),
      getCeOrganizationTopCareers({
        organizationService,
        userId,
        role,
        orgCode,
        filters,
        getMultiOrg: isMultiOrg,
      }).then(setTopCareers),
      getCeOrganizationStats({
        organizationService,
        userId,
        role,
        orgCode,
        filters,
        getMultiOrg: isMultiOrg,
      }).then((response) => setOrgStats({ isLoading: false, response })),
    ]).catch(() => {
      setOrgDailyStats({ isLoading: false });
      setOrgStats({ isLoading: false });
    });
  };

  const onApplyFilters = ({ dateRange, organization }) => {
    const { startDate, endDate } = dateRange || {};
    clearOrgStats();
    setDateRange({ startDate, endDate });
    setOrganizationSelected(organization);
  };

  const clearOrgStats = () => {
    setOrgDailyStats({ isLoading: true });
    setOrgStats({ isLoading: true });
  };

  const { loading: downloadingFile, sendRequest: exportInsights } =
    useApiRequest(() => {
      const selectedName = organizationSelected?.code
        ? organizationSelected.name
        : organizationName;
      const orgCode = organizationSelected?.code ?? organizationCode;
      const fileName = getStatsFileName(selectedName, dateRange);
      const filters = { dateRange };

      return exportOrganizationCeStats({
        organizationService,
        role,
        orgCodeOrUserId: orgCode,
        filters,
        fileName,
        getMultiOrg: !organizationSelected?.code,
      });
    }, false);

  useUnloadWarning({ trigger: downloadingFile });

  const onExportStatsData = () => {
    const { startDate, endDate } = dateRange;
    if (startDate && endDate) {
      exportInsights();
    }
  };

  const getExplorersRatings = async (career) => {
    const orgCode = organizationSelected?.code ?? organizationCode;
    const ratings = await getCareerRatings({
      organizationService,
      orgCode,
      userId,
      role,
      isMultiOrg: !organizationSelected?.code,
      learningExperienceId: career.learningExperienceId,
      dateRange,
    });

    return ratings.map((r) => {
      const { userId, rating } = r;
      const user = explorersMap.get(userId);
      return { ...user, rating };
    });
  };

  useEffect(() => {
    const { startDate, endDate } = dateRange;
    const filters = { dateRange };
    const orgCode = organizationSelected?.code ?? organizationCode;

    if (startDate && endDate) {
      getCeOrganizationSelectedStats(
        orgCode,
        filters,
        !organizationSelected?.code
      );
    }
  }, [dateRange, organizationSelected]);

  useEffect(() => {
    if (!orgStats.isLoading && !dailyOrgStats.isLoading) {
      setDashboardLoaded();
    }
  }, [orgStats, dailyOrgStats]);

  return (
    <div className="ce-multi-tab">
      <DashboardBanner product={Product.CE} />
      <CeInsights
        orgCode={organizationCode}
        generalStats={orgStats?.response}
        dailyStats={dailyOrgStats}
        onApplyFilters={onApplyFilters}
        topCareers={topCareers}
        onExportData={onExportStatsData}
        exportDataDisabled={downloadingFile}
        organizationFilterEnabled
        customExplorersFn={getExplorersRatings}
      ></CeInsights>
      <div className="notification-loader-container">
        {downloadingFile && (
          <Notification
            type={NotificationType.info}
            closable
            icon={["fa-solid", "spinner"]}
            iconConfig={{ spin: true }}
          >
            We’re preparing a download of{" "}
            <strong>{`${
              organizationSelected?.code
                ? organizationSelected.name
                : organizationName
            }'s`}</strong>{" "}
            insights. Please wait...
          </Notification>
        )}
      </div>
    </div>
  );
};
